import { ArrayToLookupByPredicate } from "global/Utility/TSFunctions";
import { fieldValueToString } from "global/Visibility/ParentVisibilityGraph";
import * as OptionSet from "model/AttributeOptionSets.type";
import { $att_$system_$bcbs_system_funding_type_change_id } from "model/ConfigAttributeConstants";
import type {
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from 'model/CRM';
import { bcbs_groupconfigurationsnapshot } from "model/CRM";
import * as ICustomTypes from "model/CRM.type";
import {
    EBenefitStatus as FAM_EBenefitStatus,
    IBenefit as FAM_IBenefit,
    IFlexibleField as FAM_IFlexibleField,
    IFlexibleFieldValue as FAM_IFlexibleFieldValue,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    TBenefitStatusOptionSetValue as FAM_TBenefitStatusOptionSetValue,
    TValue as FAM_TValue
} from "model/FlexibleAttributeModel";
import { $matt_$system_$bcbs_system_funding_type_change_id } from "model/MasterAttributeConstants";
import { v4 as uuidv4 } from 'uuid';
import { EmitEventQueue } from "./EmitEventManager";

export type TChangeTracker = Record<
    string,
    {
        field: FAM_IFlexibleField,
        fieldValue: FAM_IFlexibleFieldValue,
        value: FAM_TValue,
    }
>;

function convertEBenefitStatus(status: FAM_TBenefitStatusOptionSetValue) {

    switch (status.value) {
        case FAM_EBenefitStatus["In-Progress"]:
            return OptionSet.bcbs_groupconfigurationsnapshotevent.bcbs_benefitstatus["In Progress(808900000)"].value;

        case FAM_EBenefitStatus["Cancelled"]:
            return OptionSet.bcbs_groupconfigurationsnapshotevent.bcbs_benefitstatus['Cancelled(808900002)'].value;

        case FAM_EBenefitStatus['Released for Notification']:
            return OptionSet.bcbs_groupconfigurationsnapshotevent.bcbs_benefitstatus['Released for Notification(808900001)'].value;
    }

    return undefined;
}

export function emitEventForBenefitPropertyChange(
    snapshot: bcbs_groupconfigurationsnapshot,
    benefit: FAM_IBenefit | null,
    newBenefit: FAM_IBenefit,
    cloning?: boolean,
) {
    let emit = false;

    if (cloning || benefit?.status?.value !== newBenefit.status.value)
        if (
            newBenefit.status.value === FAM_EBenefitStatus["Released for Notification"] ||
            newBenefit.status.value === FAM_EBenefitStatus['Cancelled']
        )
            emit = true;

    if (emit) {
        // Queue
        const emitEvent: ICustomTypes.Ibcbs_groupconfigurationsnapshoteventUpdate = {
            // Benefit Name
            bcbs_benefitname: newBenefit.name,

            // Category Name
            bcbs_categoryname: newBenefit.bcbs_name!,

            // Type (Field Event, Benefit Event) default to Benefit Event
            bcbs_eventtypecode: OptionSet.bcbs_groupconfigurationsnapshotevent.bcbs_eventtypecode['Benefit Event(808900001)'].value,

            // Benefit Status
            bcbs_benefitstatus: convertEBenefitStatus(newBenefit.status),
        }

        if (cloning)
            emitEvent.bcbs_groupconfigurationsnapshoteventid = uuidv4();

        if (!emitEvent.bcbs_groupconfigurationsnapshoteventid)
            emitEvent.bcbs_groupconfigurationsnapshoteventid = uuidv4();

        emitEvent["bcbs_GroupConfigurationSnapshotid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${snapshot.bcbs_groupconfigurationsnapshotid})`;

        // ownerid
        // -----------------------------------
        if (snapshot._owningteam_value)
            emitEvent["ownerid@odata.bind"] = `/teams(${snapshot._owningteam_value})`;

        EmitEventQueue({ item: emitEvent });
    }

}

export function emitEventForFieldChange(
    config: FAM_IGroupConfigurationConfig,
    snapshot: bcbs_groupconfigurationsnapshot,
    benefit: FAM_IBenefit | null,
    field: FAM_IFlexibleField,
    fieldValue: FAM_IFlexibleFieldValue,
    newValue: FAM_TValue,
    cloning?: boolean,
    ignoreDelta?: boolean,
) {
    let emit = false;

    const configAttribute = config.cache.map_bcbs_config_attribute[field.configRef.id];

    let newValueStr: string | null = null;

    if (configAttribute?.bcbs_isemitevent) {

        newValueStr = fieldValueToString(config, newValue, false);

        if (ignoreDelta)
            emit = true;

        if (!emit) {
            const oldValueStr = fieldValueToString(config, fieldValue?.value, false);
            if (oldValueStr !== newValueStr)
                emit = true;
        }
    }

    if (configAttribute?.bcbs_isemiteventonclone)
        if (cloning)
            emit = true;

    if (emit) {

        let creating = false;

        // Queue
        const emitEvent: ICustomTypes.Ibcbs_groupconfigurationsnapshoteventUpdate = {
            bcbs_eventtypecode: OptionSet.bcbs_groupconfigurationsnapshotevent.bcbs_eventtypecode['Field Event(808900000)'].value
        }

        if (fieldValue.emitRefId)
            emitEvent.bcbs_groupconfigurationsnapshoteventid = fieldValue.emitRefId;

        if (cloning) {
            emitEvent.bcbs_groupconfigurationsnapshoteventid = uuidv4();
            creating = true;
        }

        if (!emitEvent.bcbs_groupconfigurationsnapshoteventid) {
            emitEvent.bcbs_groupconfigurationsnapshoteventid = uuidv4();
            creating = true;
        }

        if (benefit) {
            emitEvent.bcbs_benefitname = benefit.name;
            emitEvent.bcbs_categoryname = benefit.bcbs_name!;
            emitEvent.bcbs_benefitstatus = convertEBenefitStatus(benefit.status);
            // emitEvent.bcbs_eventtypecode = OptionSet.bcbs_groupconfigurationsnapshotevent.bcbs_eventtypecode['Benefit Event(808900001)'].value
        }

        if (snapshot?.bcbs_groupconfigurationsnapshotid)
            emitEvent["bcbs_GroupConfigurationSnapshotid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${snapshot.bcbs_groupconfigurationsnapshotid})`;

        if (configAttribute?.bcbs_config_attributeid)
            emitEvent["bcbs_Attributeid@odata.bind"] = `/bcbs_config_attributes(${configAttribute.bcbs_config_attributeid})`;

        if (configAttribute?._bcbs_config_masterattributeid_value)
            emitEvent["bcbs_MasterAttrributeid@odata.bind"] = `/bcbs_config_attributes(${configAttribute._bcbs_config_masterattributeid_value})`;

        emitEvent.bcbs_name = field.name;

        if (newValueStr) {
            emitEvent.bcbs_value = newValueStr;
        }

        emitEvent.bcbs_eventorigincode = OptionSet.bcbs_groupconfigurationsnapshotevent.bcbs_eventorigincode["Update(808900001)"].value;

        if (cloning)
            emitEvent.bcbs_eventorigincode = OptionSet.bcbs_groupconfigurationsnapshotevent.bcbs_eventorigincode['Clone from Parent(808900000)'].value;

        // ownerid
        // -----------------------------------
        if (snapshot._owningteam_value)
            emitEvent["ownerid@odata.bind"] = `/teams(${snapshot._owningteam_value})`;

        EmitEventQueue({ item: emitEvent, creating: creating });

        return emitEvent;
    }

}

export enum ESystemEvent {
    FundingTypeChange = "Funding Type Change",
}

export function emitEventForSystem(
    snapshot: bcbs_groupconfigurationsnapshot,
    event: ESystemEvent,
    name: string
) {

    if (!snapshot.bcbs_groupconfigurationsnapshotid)
        throw new Error("Snapshot id is required to emit system events");

    const systemEvents = {
        fundingTypeChange: {
            name: "Funding Type Change",
            bcbs_config_attributeid: $att_$system_$bcbs_system_funding_type_change_id,
            bcbs_config_masterattributeid: $matt_$system_$bcbs_system_funding_type_change_id,
        }
    }

    if (event === ESystemEvent.FundingTypeChange) {

        // const configAttribute = config.cache.map_bcbs_config_attribute[events.fundingTypeChange.bcbs_config_attributeid];

        // System - Funding Type Change
        // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&newWindow=true&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=437c57de-312d-f111-88b4-000d3a10d29d
        // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&newWindow=true&pagetype=entityrecord&etn=bcbs_config_attribute&id=8cf86947-322d-f111-88b4-000d3a10d29d

        let creating = false;

        // Queue
        const emitEvent: ICustomTypes.Ibcbs_groupconfigurationsnapshoteventUpdate = {
            bcbs_eventtypecode: OptionSet.bcbs_groupconfigurationsnapshotevent.bcbs_eventtypecode['Field Event(808900000)'].value
        }


        if (!emitEvent.bcbs_groupconfigurationsnapshoteventid) {
            emitEvent.bcbs_groupconfigurationsnapshoteventid = uuidv4();
            creating = true;
        }

        emitEvent["bcbs_GroupConfigurationSnapshotid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${snapshot.bcbs_groupconfigurationsnapshotid})`;

        emitEvent["bcbs_Attributeid@odata.bind"] = `/bcbs_config_attributes(${systemEvents.fundingTypeChange.bcbs_config_attributeid})`;

        emitEvent["bcbs_MasterAttrributeid@odata.bind"] = `/bcbs_config_attributes(${systemEvents.fundingTypeChange.bcbs_config_masterattributeid})`;

        emitEvent.bcbs_name = systemEvents.fundingTypeChange.name + " - " + name;

        emitEvent.bcbs_value = "Yes";

        emitEvent.bcbs_eventorigincode = OptionSet.bcbs_groupconfigurationsnapshotevent.bcbs_eventorigincode["Update(808900001)"].value;

        // ownerid
        // -----------------------------------
        if (snapshot._owningteam_value)
            emitEvent["ownerid@odata.bind"] = `/teams(${snapshot._owningteam_value})`;

        EmitEventQueue({ item: emitEvent, creating: creating });
    }
}

// TODO move this to emit event module
export function emitForChangeTracker(
    config: FAM_IGroupConfigurationConfig,
    snapshot: IXrmTypes_bcbs_groupconfigurationsnapshot,
    changeTracker: TChangeTracker,
    fields: FAM_IFlexibleField[],
    benefit: FAM_IBenefit | null,
) {
    const fieldsMap = ArrayToLookupByPredicate(fields, q => q.configRef.id);

    for (const [key, ct] of Object.entries(changeTracker)) {
        const targetField = fieldsMap[ct.field.configRef.id];
        const targeFieldValue = targetField.values.find(q => q.type === ct.fieldValue.type && q.tierRef?.id === ct.fieldValue?.tierRef?.id);

        if (targeFieldValue) {
            const emit = emitEventForFieldChange(
                config,
                snapshot,
                benefit,
                targetField,
                targeFieldValue,
                ct.value,
                false,
                true);

            // lets track the emitted event, so we can update later
            if (emit?.bcbs_groupconfigurationsnapshoteventid)
                targeFieldValue.emitRefId = emit.bcbs_groupconfigurationsnapshoteventid;
        }
    }
}
